import type { BetterFetch } from "@better-fetch/fetch";
import { atom } from "nanostores";
import { audienceEndpointContract } from "../../shared/contracts/audience.contracts";
import type { AudienceWithStatusType } from "../../shared/types/audience.types";
import type {
	CreateAudienceMutationRequest,
	ListAudiencesQueryParams,
	SearchAudienceMutationRequest,
	SearchAudienceResponse,
} from "../../shared/types/audienceApi.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

// Create audience management subfeature plugin
export function createAudienceManagementPlugin($fetch: BetterFetch) {
	const querySets = createQuerySetMap(audienceEndpointContract, $fetch);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			// Audience list state
			$audiencesData: atom<AudienceWithStatusType[]>([]),
			$loadingAudiences: atom<boolean>(false),
			$selectedAudience: atom<AudienceWithStatusType | null>(null),

			// Search state
			$searchParams: atom<SearchAudienceMutationRequest>({}),
			$searchResults: atom<SearchAudienceResponse | null>(null),
			$loadingSearch: atom<boolean>(false),

			// Create/Delete state
			$creatingAudience: atom<boolean>(false),
			$deletingAudience: atom<string | null>(null),
		},
		actions: {
			async listAudiences(query?: ListAudiencesQueryParams) {
				// TODO: Apply this type casting to other endpoints to prevent throw from returning error value type
				const result: typeof audienceEndpointContract.listAudiences.responseType =
					await $fetch(audienceEndpointContract.listAudiences.path, {
						method: audienceEndpointContract.listAudiences.method,
						throw: true,
						query,
					});
				return result;
			},

			async searchAudience(searchParams: SearchAudienceMutationRequest) {
				const result: typeof audienceEndpointContract.searchAudience.responseType =
					await $fetch(audienceEndpointContract.searchAudience.path, {
						method: audienceEndpointContract.searchAudience.method,
						throw: true,
						body: searchParams,
					});
				return result;
			},

			async createAudience(createParams: CreateAudienceMutationRequest) {
				const result: typeof audienceEndpointContract.createAudience.responseType =
					await $fetch(audienceEndpointContract.createAudience.path, {
						method: audienceEndpointContract.createAudience.method,
						throw: true,
						body: createParams,
					});
				return result;
			},

			async deleteAudience(audienceGroupId: string) {
				const result: typeof audienceEndpointContract.deleteAudience.responseType =
					await $fetch(
						audienceEndpointContract.deleteAudience.path.replace(
							":audienceGroupId",
							audienceGroupId,
						),
						{
							method: audienceEndpointContract.deleteAudience.method,
							throw: true,
						},
					);
				return result;
			},
		},
		querySets, // Expose for advanced usage
	};
}
